import TableSpanner from "components/TableSpanner";
import FullLoader from "components/loaders/FullLoader";
import {FC, useState} from "react";
import {useQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {IEnterpriseEntry} from "api/types";
import Enterprise from "./Enterprise";
import adminApi from "api/admin";
import {SearchIcon} from "assets/icons";
import useAwaitableModal from "hooks/useAwaitableModal";
import NewEnterpriseModal from "./modals/NewEnterprise";

const EnterpriseSettingsPage: FC = () => {
  const queryClient = useQueryClient();
  const [addEnterprise, AddEnterpriseComponent] = useAwaitableModal(NewEnterpriseModal, {});

  const {data: enterprises} = useQuery<IEnterpriseEntry[]>({
    queryKey: ["enterprises"],
    queryFn: () => adminApi.getEnterprises(),
  });

  const {mutate: updateTalentFinder, isPending: updating} = useMutation({
    mutationFn: ({id, data}: {id: string; data: {hasTalentFinder: boolean; maxEmployees: number}}) =>
      adminApi.updateEnterpriseTalentFinderAccess(id, data),
    onSuccess: (_, {id, data}) => {
      queryClient.setQueryData<IEnterpriseEntry[]>(["enterprises"], (old) =>
        old?.map((e) => (e.id === id ? {...e, ...data} : e)),
      );
    },
  });

  const onUpdateTalentFinder = (id: string, data: {hasTalentFinder: boolean; maxEmployees: number}) => {
    if (updating) return;
    updateTalentFinder({id, data});
  };

  const onCreateEnterprise = async () => {
    addEnterprise().then(async (enterprise) => {
      if (enterprise) {
        queryClient.setQueryData<IEnterpriseEntry[]>(["enterprises"], (old) => old && [enterprise, ...old]);
      }
    });
  };

  const [search, setSearch] = useState("");

  const filteredEnterprises = enterprises?.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="w-full h-full overflow-hidden pt-5 px-5 flex flex-col">
      {AddEnterpriseComponent}
      <div className="flex items-center pb-5 gap-4">
        <span className="font-semibold text-2xl text-brand-navy grow">Enterprise Management</span>
        <button className="btn-primary" onClick={onCreateEnterprise}>
          Add New Enterprise
        </button>
      </div>
      <div className="flex-cool border-t border-brand-border pt-6 relative overflow-hidden grow w-full">
        <div className="gap-4 flex items-center mobile:flex-col mobile:gap-4 mb-5">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 transform -translate-y-1/2 z-10 text-brand-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="!rounded-xl bg-brand-surface border border-brand-border py-2.5 pr-5 pl-9 text-xs text-brand-muted placeholder:opacity-60 outline-none focus:border-brand-amber transition-colors"
              placeholder="Search enterprises"
            />
          </div>
        </div>
        <div className="w-full h-full relative grow bg-white rounded-2xl border border-brand-border overflow-auto">
          <table className="talent-finder-table w-full">
            <thead className="sticky top-0 z-10 bg-brand-surface-alt">
              <th className="text-brand-muted font-medium px-3 py-2.5 text-left text-xs">Enterprise</th>
              <th className="text-brand-muted font-medium px-3 py-2.5 text-left text-xs">Added At</th>
              <th className="text-brand-muted font-medium px-3 py-2.5 text-left text-xs">Employee Count / Max</th>
              <th className="text-brand-muted font-medium px-3 py-2.5 text-left text-xs">Access to Talent Finder</th>
            </thead>
            <tbody>
              {filteredEnterprises === undefined ? (
                <TableSpanner rows={5} cols={4} children={<FullLoader />} />
              ) : filteredEnterprises.length === 0 ? (
                <TableSpanner
                  rows={5}
                  cols={4}
                  children={
                    <div className="text-brand-muted flex flex-col items-center justify-center">
                      No enterprises found
                    </div>
                  }
                />
              ) : (
                filteredEnterprises.map((enterprise) => (
                  <Enterprise key={enterprise.id} enterprise={enterprise} onUpdateTalentFinder={onUpdateTalentFinder} />
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center items-center mt-5 pt-5 border-t border-brand-border pb-5">
          <span className="text-sm text-brand-muted text-left grow">
            Total Enterprises Found:{" "}
            <span className="text-brand-navy font-medium">
              {enterprises ? (filteredEnterprises?.length ?? 0) : "..."}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseSettingsPage;
